import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MachineCreate, MachineEdit, MachineView } from '../../shared/models/machine';
import { Observable, of } from 'rxjs';
import { ApiParamConstants, ApiUrlConstants } from '../../shared/utils/api-constants';
import { ListHttpParameters } from '../../shared/models/list-http-parameters';
import { ListView } from '../../shared/models/list-view';
import { MachineCreationSuccessResponse } from '../../shared/models/machine-creation-success-response';
import { UrlConstants } from '../../shared/utils/url-constants';
import { StatusUpdateRequest } from '../../shared/models/status-update-request';
import { AppConfigService } from './app-config.service';
import { AppHttpParams } from '../../shared/models/app-http-params';
import { KeyWordTranslatorService } from './key-word-translator.service';
import { EntityStatusEnum } from '../../shared/enums/entity-status.enum';
import { AppConstants } from '../../shared/utils/app-constants';
import { Parser } from '@stomp/stompjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MachinesService {

  constructor(private http: HttpClient,
    private keyWordTranslatorService: KeyWordTranslatorService) {
  }

  create(machine: MachineCreate): Observable<MachineCreationSuccessResponse> {
    const appParams = new AppHttpParams({ responseLabel: 'machines.newMachine', notifySuccess: true });
    return this.http.post<MachineCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES, machine, { params: appParams });
  }

  update(machineId: string, machine: MachineEdit): Observable<MachineCreationSuccessResponse> {
    const appParams = new AppHttpParams({ responseLabel: 'machines.editMachine', notifySuccess: true });
    return this.http.patch<MachineCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES + UrlConstants.SEPARATOR + machineId, machine, { params: appParams });
  }

  getListView(params: ListHttpParameters): Observable<ListView<MachineView>> {
    const originalStatus = params.status;
    if (params.objectName && !params.objectId) {
      return of(null);
    }

    if (params.keyword) {
      params = this.keyWordTranslatorService.translateEnumValues(params, ApiParamConstants.LIST_ACTIVATED, EntityStatusEnum);
    }
    const userRole = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_ROLE));
    params.status = originalStatus;
    
    const httpParams = params.getHttpParams();
    if (!httpParams[ApiParamConstants.LIST_SORT]) {
      httpParams[ApiParamConstants.LIST_SORT] = 'UPDATEDATE_DESC';
    }

    const appParams = new AppHttpParams({ params: httpParams, notifyError: true });
    return this.http.get<ListView<MachineView>>(AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES, { params: appParams, headers: { 'X-User-Role': userRole } });
  }

  getOne(id: string): Observable<MachineView> {
    const appParams = new AppHttpParams({ responseLabel: 'machines.viewMachine', notifyError: true });
    return this.http.get<MachineView>(AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES + UrlConstants.SEPARATOR + id, { params: appParams });
  }

  updateStatus(machineId: string, statusUpdateRequest: StatusUpdateRequest): Observable<MachineCreationSuccessResponse> {
    const userRole = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_ROLE));
    const appParams = new AppHttpParams({ responseLabel: 'machines.updateStatus', notifyError: true, notifySuccess: true });
    return this.http.patch<MachineCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES
      + UrlConstants.SEPARATOR + machineId + ApiUrlConstants.MACHINES_STATUS, statusUpdateRequest, { params: appParams, headers: { 'X-User-Role': userRole } });
  }

  /**
   * Checks if a machine name is available for a specific Facility.
   * @param name The machine name to check
   * @param fId The facility ID
   * @returns Observable with availability status
   */
  checkMachineNameAvailability(name: string, fId: string): Observable<{ available: boolean }> {
    const appParams = new AppHttpParams({ notifyError: false });
    return this.http.get<{ available: boolean }>(
      AppConfigService.eoApiUrl + ApiUrlConstants.MACHINES + '/checkNameAvailability',
      {
        params: appParams.appendAll({
          name: name,
          fId: fId
        })
      }
    );
  }

  getMachineSuggestions(keyword: string): Observable<{ [key: string]: string[] }> {
    if (!keyword?.trim()) {
      return of({});
    }
  
    return this.http.get<{ [key: string]: string[] }>(
      `${AppConfigService.eoApiUrl}/machines/suggestions`,
      { params: new HttpParams().set('keyword', keyword).set('limit', '5') }
    ).pipe(
      catchError(() => of({}))
    );
  }

   getMachineLookup(fId?: string): Observable<any[]> {
    const url = `${AppConfigService.eoApiUrl}${AppConfigService.settings.apiServer.apiVersion}/machines/lookup`;
    let appParams = new AppHttpParams({ notifyError: true });
    
    if (fId) {
      appParams = appParams.set('fId', fId) as AppHttpParams;
    }
    
    return this.http.get<any[]>(url, { params: appParams });
  }
}
